import { createClient, SupabaseClient } from '@supabase/supabase-js';

// These environment variables will be provided by the user in Vercel
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Helper to check if Supabase is configured
export const isSupabaseConfigured = () => {
  return supabaseUrl !== '' && supabaseAnonKey !== '';
};

// Fetch wrapper with timeout to prevent hung requests
// 30s is generous enough for saves but still prevents infinite hangs
const fetchWithTimeout = (url: RequestInfo | URL, options: RequestInit = {}) => {
  const controller = new AbortController();
  const urlString = typeof url === 'string' ? url : url instanceof URL ? url.toString() : String(url);
  const isEdgeFunctionCall = urlString.includes('/functions/v1/');
  const timeoutMs = isEdgeFunctionCall ? 600000 : 30000; // 10 min for Edge Functions, 30s for REST
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  if (options.signal) {
    options.signal.addEventListener('abort', () => controller.abort());
  }

  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timeoutId));
};

// Per-name in-memory promise-chain mutex. Serialises auth operations WITHIN
// this tab (refreshes, session write-backs) so autoRefreshToken's background
// timer and Admin.tsx's 401-retry refreshSession() can never rotate the
// single-use refresh token concurrently. It never calls navigator.locks, so it
// cannot deadlock on Cloudflare Pages the way the cross-tab Web Locks API did.
// This is the missing piece behind the recurring "log out/in after every edit
// or chunk process" session drop (CC-126).
const authLockChains = new Map<string, Promise<void>>();
const authMutex = async <R>(
  name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> => {
  const prev = authLockChains.get(name) ?? Promise.resolve();
  let release!: () => void;
  const next = new Promise<void>((r) => { release = r; });
  authLockChains.set(name, next);
  await prev; // wait for the previous holder to release
  try {
    return await fn();
  } finally {
    release();
    if (authLockChains.get(name) === next) authLockChains.delete(name);
  }
};

// Only create the client if credentials are provided
// This prevents the "supabase URL is required" error
export const supabase: SupabaseClient | null = isSupabaseConfigured()
  ? createClient(supabaseUrl, supabaseAnonKey, {
      global: { fetch: fetchWithTimeout },
      auth: {
        // Bypass the Web Locks API — it deadlocks on Cloudflare Pages when an
        // orphaned lock from a prior session blocks all auth operations.
        // Auto-refresh stays on (default) so long admin sessions don't go
        // stale during uploads/chunking. Serialization is restored via an
        // in-memory promise-chain mutex (authMutex, above) so autoRefresh's
        // background timer and the 401-retry refreshSession() can't rotate the
        // single-use refresh token concurrently — the CC-126 session drop.
        lock: authMutex,
      },
    })
  : null;
